import React, { useCallback, useEffect, useRef, useState } from 'react';

import {
  LegacyCard,
  ContextualSaveBar,
  FormLayout,
  Frame,
  Icon,
  Layout,
  Page,
  Select,
  SkeletonBodyText,
  SkeletonDisplayText,
  SkeletonPage,
  TextContainer,
  TextField,
  Toast,
  Banner,
  Autocomplete,
  LegacyStack,
  Thumbnail,
  DropZone,
  Button,
} from '@shopify/polaris';

import axios, { AxiosError } from 'axios';

import { TopBarMarkup, NavigationMarkup, contextControlMarkup } from '../../../../components';

import './ProductNew.scss';
import { NoteMinor, PlusMinor, SearchMinor } from '@shopify/polaris-icons';

import { ProductFaq } from '../../../../types';
import { useUser } from '../../../../utils/PrivateRoute';
import { useNavigate } from 'react-router-dom';
import { SortableItemFaq } from '../Components';

import { closestCenter, DndContext, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy } from '@dnd-kit/sortable';

type AutocompleteCategoryOption = {
  label: string;
  value: string;
};

export function ProductNew() {
  const navigate = useNavigate();
  const { user } = useUser();

  const skipToContentRef = useRef<HTMLAnchorElement>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [buttonSpinning, setButtonSpinning] = useState(false);
  const [mobileNavigationActive, setMobileNavigationActive] = useState(false);
  const [isDirty, setIsDirty] = useState(true);
  const [active, setActive] = useState(false);

  const toggleActive = useCallback(() => setActive((active) => !active), []);

  const [saveError, setSaveError] = useState(false);

  const toggleMobileNavigationActive = useCallback(() => setMobileNavigationActive((mobileNavigationActive) => !mobileNavigationActive), []);

  const handleMobileNavigation = (data: any) => {
    setMobileNavigationActive(!data);
  };

  /**
   * Product states
   */
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');

  // FAQs
  const [productFaqs, setProductFaqs] = useState<ProductFaq[]>([]);

  // Status
  const [selectedStatus, setSelectedStatus] = useState<'active' | 'draft'>('draft');
  const statusOptions = [
    { label: 'Attivo', value: 'active' },
    { label: 'Bozza', value: 'draft' },
  ];

  // Icon
  const [icon, setIcon] = useState<File>();
  const validImageTypes = ['image/gif', 'image/jpeg', 'image/png', 'image/svg'];

  const [deselectedCategoryOptions, setDeselectedCategoryOptions] = useState<AutocompleteCategoryOption[]>([]);
  const [selectedCategoryOptions, setSelectedCategoryOptions] = useState([]);
  const [categoryInputValue, setCategoryInputValue] = useState('');
  const [categoryOptions, setCategoryOptions] = useState(deselectedCategoryOptions);
  const [categoryLoading, setCategoryLoading] = useState(false);
  const [category, setCategory] = useState('');

  // Empty fields
  const [emptyFields, setEmptyFields] = useState({
    name: false,
    description: false,
    category: false,
  });

  /**
   * Drag & Drop
   */
  const [faqs, setFaqs] = useState(['1']);
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  function handleDragEnd(event: any) {
    const { active, over } = event;

    if (active.id !== over.id) {
      setFaqs((faqs) => {
        const oldIndex = faqs.indexOf(active.id);
        const newIndex = faqs.indexOf(over.id);

        return arrayMove(faqs, oldIndex, newIndex);
      });
    }
  }

  /**
   * Fetch categories
   */
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        setCategoryLoading(true);
        const response = await fetch((process.env.REACT_APP_API_URL ? process.env.REACT_APP_API_URL : '/api') + '/admin/categories', {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('mb__access_token')}`,
          },
        });
        const data = await response.json();

        if (data.status === 'success') {
          const categoryOptions = [];
          for (const category of data.data) {
            categoryOptions.push({
              value: category._id,
              label: `${category.name}`,
            });
          }
          setCategoryOptions(categoryOptions);
        }
      } catch (error) {
        console.log(error);
      } finally {
        setCategoryLoading(false);
      }
    };
    fetchCategories();
  }, []);

  /**
   * Save data
   */
  const handleSave = useCallback(async () => {
    try {
      setButtonSpinning(true);

      // Check if fields are empty
      let errFlag = false;
      if (name === '') {
        setEmptyFields((emptyFields) => ({ ...emptyFields, name: true }));
        errFlag = true;
      }

      if (description === '') {
        setEmptyFields((emptyFields) => ({ ...emptyFields, description: true }));
        errFlag = true;
      }

      if (category === '') {
        setEmptyFields((emptyFields) => ({ ...emptyFields, category: true }));
        errFlag = true;
      }

      if (errFlag) {
        setIsLoading(false);
        return;
      }

      // Create form data
      const formData = new FormData();
      if (icon) {
        formData.append('document', icon, icon?.name);
      }
      formData.append('name', name);
      formData.append('description', description);
      formData.append('category', category);
      formData.append('status', selectedStatus);

      // Product faqs
      if (productFaqs.length > 0) {
        productFaqs.forEach((faq: ProductFaq, index: number) => {
          formData.append(`faqs[${index}][title]`, faq.title);
          formData.append(`faqs[${index}][position]`, String(faq.position));
          formData.append(`faqs[${index}][description]`, faq.description);
        });
      }

      const response = await axios.post(`${process.env.REACT_APP_API_URL ?? '/api'}/admin/products/new`, formData, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('mb__access_token')}`,
        },
      });
      const data = response.data;

      if (data.status === 'success') {
        setActive(true);
        setTimeout(() => {
          navigate(`/admin/products/${data.data._id}`);
        }, 3000);
        setIsDirty(false);
      } else {
        setSaveError(true);
      }
    } catch (error) {
      const axiosError = error as AxiosError;
      console.log(axiosError);
      setSaveError(true);
    } finally {
      setButtonSpinning(false);
    }
  }, [name, description, category, selectedStatus, icon, productFaqs]);

  /**
   * Policy Handlers
   */
  const handleNameChange = useCallback(
    (e: string) => {
      // If emptyFields.name is true, set it to false
      if (emptyFields.name) {
        setEmptyFields((emptyFields) => ({ ...emptyFields, name: false }));
      }
      setName(e);
      setIsDirty(true);
    },
    [emptyFields.name],
  );

  const handleDescriptionChange = useCallback(
    (e: string) => {
      // If emptyFields.description is true, set it to false
      if (emptyFields.description) {
        setEmptyFields((emptyFields) => ({ ...emptyFields, description: false }));
      }
      setDescription(e);
      setIsDirty(true);
    },
    [emptyFields.description],
  );

  const handleStatusChange = useCallback((e: any) => {
    setSelectedStatus(e);
    setIsDirty(true);
  }, []);

  /**
   * Category autocomplete
   */
  const updateCategoryText = useCallback(
    (value: any) => {
      setCategoryInputValue(value);

      if (!categoryLoading) {
        setCategoryLoading(true);
      }

      setTimeout(() => {
        if (value === '') {
          setCategoryOptions(deselectedCategoryOptions);
          setCategoryLoading(false);
          return;
        }
        const filterRegex = new RegExp(value, 'i');
        const resultOptions = deselectedCategoryOptions.filter((option: any) => option.label.match(filterRegex));
        setCategoryOptions(resultOptions);
        setCategoryLoading(false);
      }, 300);
    },
    [deselectedCategoryOptions, categoryLoading],
  );

  const updateCategorySelection = useCallback(
    (selected: any) => {
      // Check if emptyFields is true
      if (emptyFields.category) {
        setEmptyFields((emptyFields) => ({ ...emptyFields, category: false }));
      }

      const selectedCustomer = selected.map((selectedItem: any) => {
        const matchedOption = categoryOptions.find((option) => {
          return option.value.match(selectedItem);
        });
        return matchedOption;
      });
      setSelectedCategoryOptions(selected);
      setCategoryInputValue(selectedCustomer[0].label);

      // Add customer to order
      const category = selectedCustomer[0].value;
      setCategory(category);
    },
    [categoryOptions],
  );

  const categoryTextField = (
    <Autocomplete.TextField
      onChange={updateCategoryText}
      label="Categoria"
      value={categoryInputValue}
      prefix={<Icon source={SearchMinor as any} color="base" />}
      placeholder="Cerca una categoria"
      autoComplete="off"
      error={emptyFields.category && 'Per favore seleziona una categoria'}
    />
  );

  const emptyCategoryState = (
    <React.Fragment>
      <div style={{ textAlign: 'center' }}>
        <TextContainer>Nessuna categoria trovata</TextContainer>
      </div>
    </React.Fragment>
  );

  /**
   * Product drop zone icon
   */
  const handleDropZoneDrop = useCallback((_dropFiles: File[], acceptedFiles: File[], _rejectedFiles: File[]) => {
    setIcon(acceptedFiles[0]);
    setIsDirty(true);
  }, []);

  const fileUpload = !icon ? <DropZone.FileUpload /> : null;

  const uploadedFile = icon && (
    <div style={{ padding: '0' }}>
      <LegacyStack alignment="center">
        <Thumbnail size="large" alt={icon.name} source={NoteMinor as any} />
        <div>{icon.name}</div>
      </LegacyStack>
    </div>
  );

  const iconDropZoneMarkup = (
    <DropZone onDrop={handleDropZoneDrop} allowMultiple={false}>
      {uploadedFile}
      {fileUpload}
    </DropZone>
  );

  const removeIconAction = icon
    ? [
        {
          content: 'Rimuovi icona',
          onAction: () => setIcon(undefined),
        },
      ]
    : undefined;

  /**
   * Add faq option
   */
  const handleAddFaqOption = useCallback(() => {
    setFaqs((faqs) => [...faqs, String(faqs.length + 1)]);
  }, [faqs]);

  /**
   * Remove faq option
   */
  const handleRemoveFaq = useCallback(
    (id: string) => {
      setFaqs((faqs) => faqs.filter((faq) => faq !== id));
      // Remove the faq from the productFaqs array if it exists
      setProductFaqs((productFaqs) => {
        const newVariantOptions = productFaqs.filter((faq) => String(faq.position) !== id);
        return newVariantOptions;
      });
    },
    [setFaqs, setProductFaqs],
  );

  /**
   * Add faq option
   */
  const handleAddProductFaq = useCallback((position: string, title: string, description: string) => {
    setProductFaqs((productFaqs) => [
      ...productFaqs,
      {
        title: title,
        position: Number(position),
        description: description,
      },
    ]);
    setIsDirty(true);
  }, []);

  /**
   * Update faq option
   */
  const handleUpdateProductFaq = useCallback(
    (id: string, position: string, title: string, description: string) => {
      const tmp = productFaqs;
      const index = tmp.findIndex((faq: any) => String(faq._id) === id);
      tmp[index] = {
        position: Number(position),
        title: title,
        description: description,
      };

      setProductFaqs(tmp);
      setIsDirty(true);
    },
    [productFaqs],
  );

  /**
   * Faqs markup
   */
  const faqsMarkup = (
    <>
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={faqs} strategy={verticalListSortingStrategy}>
          {faqs.map((id, index) => (
            <SortableItemFaq
              key={id}
              id={id}
              faqItem={productFaqs[index]}
              onDelete={handleRemoveFaq}
              onSave={handleAddProductFaq}
              onUpdate={handleUpdateProductFaq}
            />
          ))}
        </SortableContext>
      </DndContext>
      <LegacyCard.Section>
        <Button plain removeUnderline icon={PlusMinor as any} onClick={handleAddFaqOption}>
          Aggiungi FAQ
        </Button>
      </LegacyCard.Section>
    </>
  );

  /**
   * Contextual Save Bar
   */
  const handleDiscard = useCallback(() => {
    setIsDirty(false);

    // Reset all the fields
    setName('');
    setDescription('');
    setSelectedStatus('draft');
    setCategory('');
  }, []);

  const contextualSaveBarMarkup = isDirty ? (
    <ContextualSaveBar
      message={'Modifiche non salvate'}
      saveAction={{
        onAction: handleSave,
        loading: buttonSpinning,
      }}
      discardAction={{
        onAction: handleDiscard,
        discardConfirmationModal: true,
      }}
      contextControl={contextControlMarkup}
    />
  ) : null;

  /**
   * Error markups & toast
   */
  const toastMarkup = active ? <Toast content="Il prodotto è stato creato con successo." onDismiss={toggleActive} /> : null;

  const saveErrorMarkup = saveError && (
    <Layout.Section>
      <Banner title="Si è verificato un errore nel salvataggio dei dati" status="critical" onDismiss={() => setSaveError(false)}>
        <p>Si è pregati di riprovare più tardi.</p>
      </Banner>
    </Layout.Section>
  );

  /**
   * Page markup
   */
  const actualPageMarkup = (
    <Page title="Aggiungi prodotto" backAction={{ content: 'Prodotti', url: '/admin/products' }}>
      <Layout>
        {/* Banner */}
        {saveErrorMarkup}

        {/* Name & description */}
        <Layout.Section>
          <LegacyCard sectioned>
            <FormLayout>
              <FormLayout>
                <TextField label="Nome" value={name} onChange={handleNameChange} autoComplete="off" error={emptyFields.name && 'Il nome è obbligatorio'} />
                <TextField
                  label="Descrizione"
                  value={description}
                  onChange={handleDescriptionChange}
                  autoComplete="off"
                  error={emptyFields.description && 'La descrizione è obbligatoria'}
                />
              </FormLayout>
            </FormLayout>
          </LegacyCard>

          {/* FAQs */}
          <LegacyCard title="FAQs">
            <LegacyCard.Section>
              <p>Aggiungi FAQ per rispondere alle domande più frequenti.</p>
            </LegacyCard.Section>
            {faqsMarkup}
          </LegacyCard>
        </Layout.Section>

        <Layout.Section secondary>
          {/* Status */}
          <LegacyCard sectioned title="Stato del prodotto">
            <Select
              label="Stato"
              options={statusOptions}
              onChange={handleStatusChange}
              value={selectedStatus}
              helpText={selectedStatus === 'active' ? 'Questo prodotto sarà disponibile sullo store.' : 'Questo prodotto sarà nascosto sullo store.'}
            />
          </LegacyCard>

          {/* Categories */}
          <LegacyCard sectioned title="Organizzazione dei prodotti">
            <FormLayout>
              <Autocomplete
                options={categoryOptions}
                selected={selectedCategoryOptions}
                onSelect={updateCategorySelection}
                loading={categoryLoading}
                textField={categoryTextField}
                emptyState={emptyCategoryState}
              />
            </FormLayout>
          </LegacyCard>

          {/* Icon */}
          <LegacyCard sectioned actions={removeIconAction} title="Icona">
            {iconDropZoneMarkup}
          </LegacyCard>
        </Layout.Section>
      </Layout>
    </Page>
  );

  // ---- Loading ----
  const loadingPageMarkup = (
    <SkeletonPage>
      <Layout>
        <Layout.Section>
          <LegacyCard sectioned>
            <TextContainer>
              <SkeletonDisplayText size="small" />
              <SkeletonBodyText lines={9} />
            </TextContainer>
          </LegacyCard>
        </Layout.Section>
      </Layout>
    </SkeletonPage>
  );

  const pageMarkup = isLoading ? loadingPageMarkup : actualPageMarkup;

  return (
    <Frame
      topBar={<TopBarMarkup user={user} handleMobileNavigation={handleMobileNavigation} />}
      navigation={<NavigationMarkup user={user} />}
      showMobileNavigation={mobileNavigationActive}
      onNavigationDismiss={toggleMobileNavigationActive}
      skipToContentTarget={skipToContentRef}
    >
      {contextualSaveBarMarkup}
      {pageMarkup}
      {toastMarkup}
    </Frame>
  );
}
